import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { API_BASE_URL } from '../App';
import { adminFetch } from '../services/adminApi';
import './EmailManagement.css';

const EmailManagement = () => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [userGroups, setUserGroups] = useState([]);
  const [emailType, setEmailType] = useState('bulk');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [emailData, setEmailData] = useState({
    subject: '',
    content: '',
    isHtml: false
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [emailHistory, setEmailHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const usersPerPage = 15;

  // Debounced search for better performance
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when searching
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const showNotification = useCallback((message, variant = 'info') => {
    enqueueSnackbar(message, {
      variant,
      autoHideDuration: variant === 'error' ? 6000 : 4000,
      anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
    });
  }, [enqueueSnackbar]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminFetch(`${API_BASE_URL}/api/admin/users`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const usersArray = data.users || [];

      // Normalize user data for consistent access
      const normalizedUsers = usersArray.map(user => ({
        id: user.id || user.uid || user._id,
        email: user.email || '',
        displayName: user.displayName || user.username || user.name || '',
        subscriptionPlan: user.subscriptionPlan || 'free',
        createdAt: user.createdAt,
        lastLoginAt: user.lastLoginAt
      }));

      setUsers(normalizedUsers);
      showNotification(`✅ Loaded ${normalizedUsers.length} users successfully`, 'success');
    } catch (error) {
      console.error('Error fetching users:', error);
      showNotification(`❌ Failed to load users: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  const fetchUserGroups = useCallback(async () => {
    try {
      const response = await adminFetch(`${API_BASE_URL}/api/admin/user-groups`);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      setUserGroups(data.groups || []);
    } catch (error) {
      console.error('Error fetching user groups:', error);
      showNotification('❌ Failed to load user groups', 'error');
    }
  }, [showNotification]);

  const fetchEmailHistory = useCallback(async () => {
    try {
      const response = await adminFetch(`${API_BASE_URL}/api/admin/email-history`);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      setEmailHistory(data.history || []);
    } catch (error) {
      console.error('Error fetching email history:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchUserGroups();
    fetchEmailHistory();
  }, [fetchUsers, fetchUserGroups, fetchEmailHistory]);

  // Optimized filtered users with memoization
  const filteredUsers = useMemo(() => {
    if (!debouncedSearchTerm) return users;

    const searchLower = debouncedSearchTerm.toLowerCase();
    return users.filter(user =>
      user.email?.toLowerCase().includes(searchLower) ||
      user.displayName?.toLowerCase().includes(searchLower) ||
      user.id?.toLowerCase().includes(searchLower)
    );
  }, [users, debouncedSearchTerm]);

  // Paginated users
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * usersPerPage;
    return filteredUsers.slice(startIndex, startIndex + usersPerPage);
  }, [filteredUsers, currentPage, usersPerPage]);

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Improved user selection handlers
  const handleUserSelect = useCallback((userId) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const currentPageIds = paginatedUsers.map(user => user.id);
    const allSelected = currentPageIds.every(id => selectedUsers.has(id));

    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselect all on current page
        currentPageIds.forEach(id => newSet.delete(id));
      } else {
        // Select all on current page
        currentPageIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  }, [paginatedUsers, selectedUsers]);

  const clearAllSelections = useCallback(() => {
    setSelectedUsers(new Set());
  }, []);

  const getRecipientCount = () => {
    switch (emailType) {
      case 'bulk':
        return users.length;
      case 'selected':
        return selectedUsers.size;
      case 'group':
        const group = userGroups.find(g => g.id === selectedGroup);
        return group?.userCount || 0;
      default:
        return 0;
    }
  };

  const testEmailConfiguration = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`${API_BASE_URL}/api/admin/test-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        showNotification(
          `🎉 ${result.message} - Check your email inbox!`,
          'success'
        );
      } else {
        const suggestions = result.suggestions?.join('\n• ') || '';
        showNotification(
          `❌ Email test failed: ${result.error}\n\nSuggestions:\n• ${suggestions}`,
          'error'
        );
      }
    } catch (error) {
      console.error('Error testing email:', error);
      showNotification(`❌ Failed to test email configuration: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const sendEmail = async () => {
    if (!emailData.subject.trim() || !emailData.content.trim()) {
      showNotification('⚠️ Please fill in both subject and content', 'warning');
      return;
    }

    if (emailType === 'selected' && selectedUsers.size === 0) {
      showNotification('⚠️ Please select at least one user', 'warning');
      return;
    }

    if (emailType === 'group' && !selectedGroup) {
      showNotification('⚠️ Please select a user group', 'warning');
      return;
    }

    const recipientCount = getRecipientCount();
    if (recipientCount === 0) {
      showNotification('⚠️ No recipients selected', 'warning');
      return;
    }

    const confirmed = window.confirm(
      `Send email "${emailData.subject}" to ${recipientCount} recipients?`
    );

    if (!confirmed) return;

    setLoading(true);
    try {
      const payload = {
        type: emailType,
        subject: emailData.subject.trim(),
        content: emailData.content.trim(),
        isHtml: emailData.isHtml,
        selectedUsers: emailType === 'selected' ? Array.from(selectedUsers) : [],
        selectedGroup: emailType === 'group' ? selectedGroup : null
      };

      const response = await adminFetch(`${API_BASE_URL}/api/admin/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        showNotification(`🎉 Email sent successfully to ${result.recipientCount} recipients!`, 'success');
        setEmailData({ subject: '', content: '', isHtml: false });
        clearAllSelections();
        setSelectedGroup('');
        fetchEmailHistory();
      } else {
        showNotification(`❌ Failed to send email: ${result.message}`, 'error');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      showNotification(`❌ Error sending email: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="email-management">
      <div className="email-header">
        <h2>📧 Email Management</h2>
        <div className="header-actions">
          <button
            className="btn-secondary"
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? '✏️ Compose' : '📚 History'}
          </button>
          <button
            className="btn-secondary"
            onClick={fetchUsers}
            disabled={loading}
          >
            🔄 {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {!showHistory ? (
        <div className="email-composer">
          {/* Email Type Selection */}
          <div className="email-type-section">
            <h3>📋 Recipients</h3>
            <div className="email-type-tabs">
              <button
                className={`tab ${emailType === 'bulk' ? 'active' : ''}`}
                onClick={() => setEmailType('bulk')}
              >
                🌍 All Users ({users.length})
              </button>
              <button
                className={`tab ${emailType === 'selected' ? 'active' : ''}`}
                onClick={() => setEmailType('selected')}
              >
                👥 Selected Users ({selectedUsers.size})
              </button>
              <button
                className={`tab ${emailType === 'group' ? 'active' : ''}`}
                onClick={() => setEmailType('group')}
              >
                🏷️ User Groups
              </button>
            </div>
          </div>

          {/* Group Selection */}
          {emailType === 'group' && (
            <div className="group-selection">
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="group-select"
              >
                <option value="">Select a group...</option>
                {userGroups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.name} ({group.userCount} users)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* User Selection */}
          {emailType === 'selected' && (
            <div className="user-selection">
              <div className="user-search-header">
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="🔍 Search by email, name, or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  {searchTerm && (
                    <button
                      className="clear-search"
                      onClick={() => setSearchTerm('')}
                    >
                      ×
                    </button>
                  )}
                </div>

                <div className="selection-actions">
                  <button
                    onClick={handleSelectAll}
                    className="btn-secondary"
                    disabled={paginatedUsers.length === 0}
                  >
                    {paginatedUsers.every(user => selectedUsers.has(user.id)) ?
                      '❌ Deselect Page' : '✅ Select Page'}
                  </button>
                  {selectedUsers.size > 0 && (
                    <button
                      onClick={clearAllSelections}
                      className="btn-danger"
                    >
                      🗑️ Clear All ({selectedUsers.size})
                    </button>
                  )}
                </div>
              </div>

              <div className="users-summary">
                Showing {paginatedUsers.length} of {filteredUsers.length} users
                {debouncedSearchTerm && ` (filtered by "${debouncedSearchTerm}")`}
              </div>

              <div className="user-list">
                {paginatedUsers.length === 0 ? (
                  <div className="no-users">
                    {loading ? '⏳ Loading users...' :
                     debouncedSearchTerm ? '🔍 No users found matching your search' :
                     '👥 No users available'}
                  </div>
                ) : (
                  paginatedUsers.map(user => (
                    <div
                      key={user.id}
                      className={`user-item ${selectedUsers.has(user.id) ? 'selected' : ''}`}
                      onClick={() => handleUserSelect(user.id)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.has(user.id)}
                        onChange={() => handleUserSelect(user.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="user-info">
                        <div className="user-primary">
                          <span className="user-email">{user.email}</span>
                          <span className={`user-plan ${user.subscriptionPlan}`}>
                            {user.subscriptionPlan || 'free'}
                          </span>
                        </div>
                        <div className="user-secondary">
                          {user.displayName && (
                            <span className="user-name">{user.displayName}</span>
                          )}
                          <span className="user-id">ID: {user.id.substring(0, 8)}...</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Enhanced Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <div className="pagination-info">
                    Page {currentPage} of {totalPages}
                    ({selectedUsers.size} selected total)
                  </div>
                  <div className="pagination-controls">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="btn-pagination"
                    >
                      ⏮️
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="btn-pagination"
                    >
                      ⬅️ Prev
                    </button>
                    <span className="page-display">{currentPage}</span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="btn-pagination"
                    >
                      Next ➡️
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="btn-pagination"
                    >
                      ⏭️
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Enhanced Email Composer */}
          <div className="email-composer-section">
            <h3>✍️ Compose Email</h3>
            <div className="recipient-summary">
              <strong>📬 Recipients: {getRecipientCount()} users</strong>
              {emailType === 'selected' && selectedUsers.size > 0 && (
                <span className="selection-count">({selectedUsers.size} selected)</span>
              )}
            </div>

            <div className="form-group">
              <label>📝 Subject</label>
              <input
                type="text"
                value={emailData.subject}
                onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Enter email subject..."
                className="email-input"
                maxLength={200}
              />
              <small>{emailData.subject.length}/200 characters</small>
            </div>

            <div className="form-group">
              <div className="content-header">
                <label>📄 Content</label>
                <label className="html-toggle">
                  <input
                    type="checkbox"
                    checked={emailData.isHtml}
                    onChange={(e) => setEmailData(prev => ({ ...prev, isHtml: e.target.checked }))}
                  />
                  <span>HTML Format</span>
                </label>
              </div>
              <textarea
                value={emailData.content}
                onChange={(e) => setEmailData(prev => ({ ...prev, content: e.target.value }))}
                placeholder={emailData.isHtml ?
                  "Enter HTML content... <h1>Example</h1><p>Your message here</p>" :
                  "Enter your message here..."}
                className="email-textarea"
                rows={12}
                maxLength={10000}
              />
              <small>{emailData.content.length}/10,000 characters</small>
            </div>

            <div className="send-section">
              <button
                onClick={sendEmail}
                disabled={loading || getRecipientCount() === 0 || !emailData.subject.trim() || !emailData.content.trim()}
                className="btn-primary send-btn"
              >
                {loading ? '📤 Sending...' : `📧 Send Email to ${getRecipientCount()} Recipients`}
              </button>

              <button
                onClick={testEmailConfiguration}
                disabled={loading}
                className="btn-secondary"
              >
                🧪 Test Email Setup
              </button>

              {(emailData.subject.trim() || emailData.content.trim()) && (
                <button
                  onClick={() => setEmailData({ subject: '', content: '', isHtml: false })}
                  className="btn-secondary"
                >
                  🗑️ Clear Form
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="email-history">
          <h3>📚 Email History</h3>
          {emailHistory.length === 0 ? (
            <div className="no-history">
              <p>📭 No emails sent yet.</p>
              <button
                onClick={() => setShowHistory(false)}
                className="btn-primary"
              >
                ✏️ Send Your First Email
              </button>
            </div>
          ) : (
            <div className="history-list">
              {emailHistory.map(email => (
                <div key={email.id} className="history-item">
                  <div className="history-header">
                    <strong>{email.subject}</strong>
                    <span className="history-date">
                      📅 {new Date(email.sentAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="history-details">
                    <span className="history-type">📋 Type: {email.type}</span>
                    <span className="history-recipients">👥 Recipients: {email.recipientCount}</span>
                    <span className={`history-status ${email.status}`}>
                      {email.status === 'success' ? '✅' : '❌'} {email.status}
                    </span>
                  </div>
                  <div className="history-preview">
                    {email.content.substring(0, 150)}
                    {email.content.length > 150 && '...'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailManagement;
